// Copy / paste / duplicate of drawn conditions — the pure part.
//
// A drawn condition is a catalog type (Item / Assembly) plus where it was drawn. Copying one keeps the type and
// the shape and lets the person put it somewhere else: pasted at the cursor (on the drawing they are looking at,
// which may be a different one — geometry is in real feet, so it keeps its size) or duplicated just beside the
// original. Only what a person drew can be copied:
//   machine rows  (generated HEPA / light symbols, rule-applied walls) are re-made by their engines
//   back-outs     (MeasureRole 'deduct') belong to one Room Element's area
//   rows with no geometry (manual conditions) have nothing to place
// The creation itself is RoomTakeoff's (one undo step); this module decides WHAT and WHERE.

export const DUPLICATE_OFFSET_FT = 2;

const geometryOf = (row) => { try { const g = row?.GeometryJson; return (typeof g === "string" ? JSON.parse(g || "{}") : g) || {}; } catch { return {}; } };
const num = (v) => (Number.isFinite(+v) ? +v : null);

/** The shape's points: a placed symbol is one point, a run / area its vertices. */
export function pointsOfRow(row) {
  const g = geometryOf(row);
  if (row?.Style === "count") return num(g.x) != null && num(g.y) != null ? [{ x: +g.x, y: +g.y }] : [];
  return Array.isArray(g.points) ? g.points.filter(p => num(p?.x) != null && num(p?.y) != null).map(p => ({ x: +p.x, y: +p.y })) : [];
}

/** Which of these rows can be copied; and how many were left out, by reason. */
export function copyableRows(rows) {
  const out = [], skipped = { machine: 0, backOut: 0, noGeometry: 0 };
  for (const r of rows || []) {
    if (r.AutoKey || r.AutoWallKey || r.AutoSystem) skipped.machine += 1;
    else if (r.MeasureRole === "deduct") skipped.backOut += 1;
    else if (!pointsOfRow(r).length) skipped.noGeometry += 1;
    else out.push(r);
  }
  return { rows: out, skipped };
}

/** The middle of the copied shapes' bounding box — the point a paste puts under the cursor. */
export function clipboardAnchor(rows) {
  const pts = (rows || []).flatMap(pointsOfRow);
  if (!pts.length) return null;
  const xs = pts.map(p => p.x), ys = pts.map(p => p.y);
  return { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: (Math.min(...ys) + Math.max(...ys)) / 2 };
}

const r2 = (v) => Math.round(v * 100) / 100;

/** GeometryJson moved by (dx, dy) feet. Everything else in it (width, angle, flip, tile result…) is kept. */
export function translateGeometryJson(geometryJson, dx, dy) {
  const g = geometryOf({ GeometryJson: geometryJson });
  const out = { ...g };
  if (num(g.x) != null && num(g.y) != null && !Array.isArray(g.points)) { out.x = r2(+g.x + dx); out.y = r2(+g.y + dy); }
  if (Array.isArray(g.points)) out.points = g.points.map(p => ({ ...p, x: r2(+p.x + dx), y: r2(+p.y + dy) }));
  return JSON.stringify(out);
}

/**
 * What to create for a paste at `at`, or a duplicate when `offset` is given: [{ row, dx, dy, geometryJson }].
 * The group keeps its layout — every row moves by the same (dx, dy).
 */
export function pasteItems(rows, { at = null, offset = null } = {}) {
  const anchor = clipboardAnchor(rows);
  if (!anchor) return [];
  const dx = offset ? offset.dx : (at ? at.x - anchor.x : DUPLICATE_OFFSET_FT);
  const dy = offset ? offset.dy : (at ? at.y - anchor.y : DUPLICATE_OFFSET_FT);
  return rows.map(row => ({ row, dx, dy, geometryJson: translateGeometryJson(row.GeometryJson, dx, dy) }));
}

/** "Copied 3 conditions" / "…(2 generated and 1 back-out left out)". */
export function copyNotice(count, skipped = {}) {
  const left = [];
  if (skipped.machine) left.push(`${skipped.machine} generated`);
  if (skipped.backOut) left.push(`${skipped.backOut} back-out${skipped.backOut === 1 ? "" : "s"}`);
  if (skipped.noGeometry) left.push(`${skipped.noGeometry} with no drawn shape`);
  const base = count ? `Copied ${count} condition${count === 1 ? "" : "s"}` : "Nothing to copy";
  return left.length ? `${base} (${left.join(", ")} left out — those can't be copied)` : base;
}
